import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toggleIncidentCreateModalAction } from '../../../redux/slices/analyses';
import { ReferenceLine } from 'recharts';
import CustomReferenceLineLabel from '../CustomReferenceLineLabel';
import { isEmpty, isEqual, isNil } from 'lodash';
import moment from 'moment';
import { TimeUnit } from '../../../utils/apollo/graphqlSchema';
import { IIncident } from '../../../pages/Incidents/interfaces';
import { YAxisPosition } from '../interfaces';
import { IconName } from '../../Icon';
import { globalFormat } from '../../../utils/formatting';
import { CHART_DATE_FORMAT } from '../../../config/constants';
import { theme } from '../../../styles/theme';
import { useChartContext } from '../ChartContext';

interface IUseIncidentsParams {
  analysisId: string;
  incidents: Record<string, IIncident[]>;
  yAxisId: number | YAxisPosition | undefined;
  createIncidentData: any; // TODO - add type info
  isCreateIncidentModalOpen: boolean;
  chartDates: string[];
  activeIncidentTypes: Record<string, boolean>;
}

export default function useIncidents({
  analysisId,
  incidents,
  yAxisId,
  createIncidentData,
  isCreateIncidentModalOpen,
  chartDates,
  activeIncidentTypes,
}: IUseIncidentsParams) {
  const dispatch = useDispatch();
  const {
    metadata: { timeUnit },
  } = useChartContext();

  const [activeIndex, setActiveIndex] = useState(0);
  const [infoPopupIncidentKey, setInfoPopupIncidentKey] = useState('');

  const closeCreateIncident = useCallback(() => {
    dispatch(
      toggleIncidentCreateModalAction({
        analysisId,
        modalState: false,
        modalData: {},
      }),
    );
  }, [dispatch, analysisId]);

  const renderCreateIncidentReferenceLine = useMemo(
    () =>
      !isEmpty(createIncidentData) &&
      !isNil(yAxisId) && (
        <ReferenceLine
          x={createIncidentData.date}
          stroke={theme.colors.secondary800}
          yAxisId={yAxisId}
          ifOverflow={'extendDomain'}
          label={
            <CustomReferenceLineLabel
              incidentKey={createIncidentData?.id}
              createIncident
              analysisId={analysisId}
              selectedDate={moment(createIncidentData.date).toDate()}
              isCreateIncidentModalOpen={isCreateIncidentModalOpen}
              closeCreateIncident={closeCreateIncident}
            />
          }
          strokeWidth={2}
        />
      ),
    [
      analysisId,
      yAxisId,
      createIncidentData,
      isCreateIncidentModalOpen,
      closeCreateIncident,
    ],
  );

  const onInfoPopupChange = (key: string) => {
    setInfoPopupIncidentKey(key);
    setActiveIndex(0);
  };

  // initially the reference line had a gap from the incident icon.
  // "shape" is a native prop of ReferenceLine that allows us to generate
  // a custom shape as the reference line. we will use this to extend the reference line
  // and connect the incident icon
  const CustomShape = (props: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  }) => {
    const { x1, y1, x2, y2, ...rest } = props;
    return <line x1={x1} y1={y1 + 2} x2={x2} y2={y2 - 2} {...rest} />;
  };

  const renderIncidents = useMemo(() => {
    if (isEmpty(incidents)) {
      return null;
    }

    const parsedIncidents: Record<string, IIncident[]> = {};
    Object.keys(incidents).map((incidentKey) => {
      // XXX incidentKey is in YYYY-MM-DD format, our chart dates are in M/D/YYYY
      const currentKeyMoment = moment(incidentKey);
      let currentKey = globalFormat.formatDate(incidentKey, CHART_DATE_FORMAT);
      /* If the chart current configuration does not contain the event date as a label
        find the closest NEXT date and assign it as an event key */
      if (!chartDates.includes(currentKey)) {
        const chartDateAfterCurrentKey = chartDates.find((chartDate: string) =>
          moment(chartDate, CHART_DATE_FORMAT).isSameOrAfter(currentKeyMoment),
        );
        if (chartDateAfterCurrentKey) {
          currentKey = chartDateAfterCurrentKey;
        } else {
          currentKey = chartDates[chartDates.length - 1];
        }
      }
      const filteredIncidentsByType = incidents[incidentKey].filter(
        (singleIncident: IIncident) =>
          activeIncidentTypes[singleIncident?.type?.id],
      );
      if (parsedIncidents[currentKey]) {
        parsedIncidents[currentKey] = [
          ...parsedIncidents[currentKey],
          ...filteredIncidentsByType,
        ];
        return null;
      }
      parsedIncidents[currentKey] = [...filteredIncidentsByType];
      return null;
    });

    return (
      timeUnit !== TimeUnit.AllTime &&
      Object.keys(parsedIncidents).map((incidentKey: string) => {
        const currentIncidents = parsedIncidents[incidentKey];
        if (currentIncidents.length && !isNil(yAxisId)) {
          const currIndex = isEqual(infoPopupIncidentKey, incidentKey)
            ? activeIndex
            : 0;

          const currentIncident = currentIncidents[currIndex];
          const currentIncidentType = currentIncident?.type;
          const currentIncidentIcon = currentIncidentType?.icon
            ? currentIncidentType.icon
            : 'incidents';

          return (
            <ReferenceLine
              key={currentIncident?.id}
              x={incidentKey}
              stroke={currentIncident?.type?.color}
              yAxisId={yAxisId}
              ifOverflow={'extendDomain'}
              shape={CustomShape}
              label={
                <CustomReferenceLineLabel
                  currentIncident={currentIncident}
                  incidents={currentIncidents}
                  incidentIcon={currentIncidentIcon as IconName}
                  incidentColor={currentIncidentType?.color}
                  analysisId={analysisId}
                  incidentKey={incidentKey}
                  activeIndex={currIndex}
                  setActiveIndex={setActiveIndex}
                  isInfoPopupOpen={infoPopupIncidentKey === incidentKey}
                  setInfoPopupOpen={onInfoPopupChange}
                />
              }
              strokeWidth={1}
            />
          );
        }
        return null;
      })
    );
  }, [
    analysisId,
    yAxisId,
    activeIncidentTypes,
    incidents,
    chartDates,
    timeUnit,
    activeIndex,
    infoPopupIncidentKey,
  ]);

  return { renderIncidents, renderCreateIncidentReferenceLine };
}
